import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline'

const MAX_RESULTS = 50

const normalize = value => String(value ?? '')
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .toLowerCase()
  .trim()

const workerName = worker => `${worker.firstName || ''} ${worker.lastName || ''}`.trim()

// Workers are still identified by their legacy id (old_id); fall back to the
// internal worker_id only when a worker has no legacy id.
const workerCode = worker => String(worker.oldId || worker.workerId)

export const workerLabel = worker => (worker ? `${workerCode(worker)} - ${workerName(worker)}` : '')

// Matches the term against old_id and worker_id first (exact, prefix, contains)
// and then against the worker name, ranking id matches on top.
export const searchWorkers = (workers, query, limit = MAX_RESULTS) => {
  const term = normalize(query)
  if (!term) return workers.slice(0, limit)

  const matches = []
  for (const worker of workers) {
    const ids = [worker.oldId, worker.workerId]
      .filter(id => id !== null && id !== undefined && id !== '')
      .map(normalize)

    let rank = null
    if (ids.some(id => id === term)) rank = 0
    else if (ids.some(id => id.startsWith(term))) rank = 1
    else if (ids.some(id => id.includes(term))) rank = 2
    else if (normalize(workerName(worker)).includes(term)) rank = 3

    if (rank !== null) matches.push({ worker, rank })
  }

  return matches
    .sort((a, b) => a.rank - b.rank)
    .slice(0, limit)
    .map(({ worker }) => worker)
}

const WorkerTypeahead = ({ id = 'workerId', name = 'workerId', workers = [], value, onChange, placeholder }) => {
  const { t } = useTranslation()
  const inputRef = useRef(null)
  const listRef = useRef(null)
  const listId = `${id}-options`

  const selectedWorker = useMemo(
    () => workers.find(worker => String(worker.workerId) === String(value)) || null,
    [workers, value]
  )

  const [inputValue, setInputValue] = useState(workerLabel(selectedWorker))
  const [searchTerm, setSearchTerm] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const [position, setPosition] = useState(null)

  // Keep the text in sync when the selection changes from outside (edit, clear).
  useEffect(() => {
    setInputValue(workerLabel(selectedWorker))
  }, [selectedWorker])

  const results = useMemo(() => searchWorkers(workers, searchTerm), [workers, searchTerm])

  // The list is rendered in a portal with fixed positioning so the modal's
  // scroll container does not clip it; follow the input on scroll/resize.
  useLayoutEffect(() => {
    if (!isOpen) return

    const updatePosition = () => {
      const rect = inputRef.current?.getBoundingClientRect()
      if (rect) setPosition({ top: rect.bottom + 4, left: rect.left, width: rect.width })
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)

    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    listRef.current?.children[activeIndex]?.scrollIntoView?.({ block: 'nearest' })
  }, [activeIndex, isOpen])

  const emitChange = nextValue => onChange({ target: { name, value: nextValue } })

  const openList = () => {
    setSearchTerm('')
    setActiveIndex(0)
    setIsOpen(true)
  }

  const selectWorker = worker => {
    emitChange(worker.workerId)
    setInputValue(workerLabel(worker))
    setIsOpen(false)
  }

  const clearSelection = () => {
    emitChange('')
    setInputValue('')
    setSearchTerm('')
    inputRef.current?.focus()
  }

  const handleInputChange = event => {
    setInputValue(event.target.value)
    setSearchTerm(event.target.value)
    setActiveIndex(0)
    setIsOpen(true)
  }

  const handleFocus = event => {
    event.target.select()
    openList()
  }

  const handleBlur = () => {
    setIsOpen(false)
    if (!inputValue.trim() && selectedWorker) {
      emitChange('')
      return
    }
    setInputValue(workerLabel(selectedWorker))
  }

  const handleKeyDown = event => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      if (!isOpen) return openList()
      setActiveIndex(index => Math.min(index + 1, results.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex(index => Math.max(index - 1, 0))
    } else if (event.key === 'Enter') {
      // Never submit the surrounding form from the search box.
      event.preventDefault()
      if (isOpen && results[activeIndex]) selectWorker(results[activeIndex])
    } else if (event.key === 'Escape' && isOpen) {
      // Close only the list, not the modal that hosts it.
      event.stopPropagation()
      setIsOpen(false)
      setInputValue(workerLabel(selectedWorker))
    }
  }

  return (
    <div className='relative mt-1'>
      <input
        ref={inputRef}
        id={id}
        type='text'
        role='combobox'
        aria-expanded={isOpen}
        aria-controls={listId}
        aria-autocomplete='list'
        aria-activedescendant={isOpen && results[activeIndex] ? `${listId}-${results[activeIndex].workerId}` : undefined}
        autoComplete='off'
        value={inputValue}
        placeholder={placeholder || t('mgmt.stickerModal.workerSearch')}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className='w-full p-2 pr-8 border rounded-md shadow-sm text-sm focus:outline-none focus:ring-1 focus:border-green-800 focus:ring-green-500'
      />
      {selectedWorker
        ? (
          <button
            type='button'
            onMouseDown={event => event.preventDefault()}
            onClick={clearSelection}
            aria-label={t('mgmt.stickerModal.clearWorker')}
            className='absolute inset-y-0 right-2 flex items-center text-gray-400 hover:text-gray-600'
          >
            <XMarkIcon className='w-4 h-4' />
          </button>
          )
        : (
          <div className='pointer-events-none absolute inset-y-0 right-2 flex items-center'>
            <ChevronDownIcon className='w-4 h-4 text-gray-400' />
          </div>
          )}

      {isOpen && position && createPortal(
        <ul
          ref={listRef}
          id={listId}
          role='listbox'
          style={{ position: 'fixed', top: position.top, left: position.left, width: position.width, zIndex: 60 }}
          className='max-h-60 overflow-y-auto rounded-md border bg-white py-1 text-sm shadow-lg'
          onMouseDown={event => event.preventDefault()}
        >
          {results.length === 0 && (
            <li className='px-3 py-2 text-gray-500'>{t('mgmt.stickerModal.noWorkersFound')}</li>
          )}
          {results.map((worker, index) => {
            const isSelected = String(worker.workerId) === String(value)
            return (
              <li
                key={worker.workerId}
                id={`${listId}-${worker.workerId}`}
                role='option'
                aria-selected={isSelected}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => selectWorker(worker)}
                className={`flex cursor-pointer gap-2 px-3 py-2 ${index === activeIndex ? 'bg-green-50' : ''} ${isSelected ? 'font-semibold text-greenPrimary' : 'text-gray-700'}`}
              >
                <span className='min-w-[3.5rem] text-gray-500'>{workerCode(worker)}</span>
                <span className='truncate'>{workerName(worker)}</span>
              </li>
            )
          })}
        </ul>,
        document.body
      )}
    </div>
  )
}

WorkerTypeahead.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  workers: PropTypes.arrayOf(PropTypes.shape({
    workerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    oldId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    firstName: PropTypes.string,
    lastName: PropTypes.string
  })),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string
}

export default WorkerTypeahead
